import { StandardEvents } from '@shopify/events';

(() => {
  if (window.KervelanCartDrawerReference?.destroy) {
    window.KervelanCartDrawerReference.destroy();
  }

  const controller = new AbortController();
  const { signal } = controller;
  const backdrop = document.querySelector('[data-kv-cart-reference-backdrop]');
  const triggerSelector = '.kv-actions [aria-label="Cart"]';
  let hideTimer = null;
  let noteTimer = null;
  let drawerObserver = null;
  let observedDrawer = null;

  const rootPath = window.Shopify?.routes?.root || '/';
  const route = (path) => `${rootPath}${path}`.replace(/\/{2,}/g, '/');

  const getDrawer = () => document.getElementById('cart-drawer');

  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const updateCartCount = (count) => {
    const display = count > 99 ? '99+' : String(Math.max(0, count));

    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      let bubble = trigger.querySelector('.kv-cart-count');

      if (!bubble) {
        bubble = document.createElement('span');
        bubble.className = 'kv-cart-count';
        bubble.setAttribute('aria-hidden', 'true');
        trigger.appendChild(bubble);
      }

      bubble.hidden = count <= 0;
      bubble.textContent = count > 0 ? display : '';
    });
  };

  const syncCheckoutLabelFromDom = () => {
    const summary = getDrawer()?.querySelector('.cart-drawer__summary');
    const label = getDrawer()?.querySelector('.cart__checkout-button .button-text');
    if (!(summary instanceof HTMLElement) || !(label instanceof HTMLElement)) return;

    const totalCents = Number(summary.dataset.kvCartTotal);
    if (!Number.isFinite(totalCents)) return;

    const desired = `CHECKOUT  •  ${formatMoney(totalCents)}`;
    if (label.textContent?.trim() !== desired) label.textContent = desired;
  };

  const syncTaxNoteFromDom = () => {
    const summary = getDrawer()?.querySelector('.cart-drawer__summary');
    const source = summary?.querySelector('.cart-totals__tax-note');
    const note = summary?.querySelector('.kv-cart-tax-note');
    if (!(source instanceof HTMLElement) || !(note instanceof HTMLElement)) return;

    const desired = source.textContent?.replace(/\s+/g, ' ').trim() || '';
    if (note.textContent !== desired) note.textContent = desired;
  };

  const syncNoteFromDom = () => {
    const summary = getDrawer()?.querySelector('.cart-drawer__summary');
    const textarea = getDrawer()?.querySelector('.kv-cart-note__input');

    if (!(summary instanceof HTMLElement) || !(textarea instanceof HTMLTextAreaElement)) return;
    if (document.activeElement === textarea) return;

    const note = summary.dataset.kvCartNote || '';
    if (textarea.value !== note) textarea.value = note;
  };

  const saveNote = (value) => {
    clearTimeout(noteTimer);

    const summary = getDrawer()?.querySelector('.cart-drawer__summary');
    if (summary instanceof HTMLElement) summary.dataset.kvCartNote = value;

    noteTimer = window.setTimeout(async () => {
      try {
        await fetch(route('cart/update.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ note: value }),
        });
      } catch (_) {}
    }, 450);
  };

  const ensureMeta = () => {
    const summary = getDrawer()?.querySelector('.cart-drawer__summary');
    if (!(summary instanceof HTMLElement)) return false;

    const existing = summary.querySelector('.kv-cart-meta');
    if (existing) {
      syncNoteFromDom();
      return false;
    }

    const meta = document.createElement('div');
    meta.className = 'kv-cart-meta';
    meta.innerHTML = `
      <details class="kv-cart-note">
        <summary>Add order note</summary>
        <div class="kv-cart-note__body">
          <label class="visually-hidden" for="kv-cart-note-input">Add order note</label>
          <textarea id="kv-cart-note-input" class="kv-cart-note__input" rows="3"></textarea>
        </div>
      </details>
      <div class="kv-cart-tax-note"></div>
    `;

    const ctas = summary.querySelector('.cart__ctas');
    summary.insertBefore(meta, ctas || null);

    const textarea = meta.querySelector('.kv-cart-note__input');
    if (textarea instanceof HTMLTextAreaElement) {
      textarea.value = summary.dataset.kvCartNote || '';
      textarea.addEventListener('input', () => saveNote(textarea.value), { signal });
    }

    return true;
  };

  const syncPresentationFromDom = () => {
    ensureMeta();
    syncTaxNoteFromDom();
    syncCheckoutLabelFromDom();
    syncNoteFromDom();
  };

  const setOpenState = (open) => {
    clearTimeout(hideTimer);
    document.documentElement.classList.toggle('kv-cart-open', open);
    document.body?.classList.toggle('kv-cart-open', open);

    if (!backdrop) {
      if (open) syncPresentationFromDom();
      return;
    }

    if (open) {
      backdrop.hidden = false;
      requestAnimationFrame(() => backdrop.classList.add('is-visible'));
      syncPresentationFromDom();
    } else {
      backdrop.classList.remove('is-visible');
      hideTimer = window.setTimeout(() => {
        if (!document.documentElement.classList.contains('kv-cart-open')) backdrop.hidden = true;
      }, 230);
    }
  };

  const decorateTriggers = () => {
    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'cart-drawer');
    });
  };

  const attachObserver = () => {
    const drawer = getDrawer();
    if (!drawer || drawer === observedDrawer) return;

    drawerObserver?.disconnect();
    observedDrawer = drawer;

    drawerObserver = new MutationObserver(() => {
      syncPresentationFromDom();
    });

    drawerObserver.observe(drawer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-kv-cart-total', 'data-kv-cart-note'],
    });
  };

  const onCartLinesUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const count = Number(cart?.totalQuantity);
        if (Number.isFinite(count)) updateCartCount(count);

        // The cart section morph is owned by CartItemsComponent. Presentation
        // is resynced from the morphed DOM, so no second cart request is needed.
        requestAnimationFrame(syncPresentationFromDom);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[KERVELAN] Cart presentation update failed', error);
        }
      });
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate, { signal });

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    setOpenState(true);
  }, { signal });

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    setOpenState(false);
  }, { signal });

  backdrop?.addEventListener('click', () => {
    const drawer = getDrawer();
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }, { signal });

  backdrop?.addEventListener('wheel', (event) => event.preventDefault(), { passive: false, signal });
  backdrop?.addEventListener('touchmove', (event) => event.preventDefault(), { passive: false, signal });

  document.addEventListener('shopify:section:load', () => {
    decorateTriggers();
    attachObserver();
    syncPresentationFromDom();
  }, { signal });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    decorateTriggers();
    attachObserver();
    syncPresentationFromDom();
  }, { signal });

  decorateTriggers();
  attachObserver();
  syncPresentationFromDom();

  const initialDrawer = getDrawer();
  if (initialDrawer?.hasAttribute('open')) setOpenState(true);

  window.KervelanCartDrawerReference = {
    destroy() {
      controller.abort();
      drawerObserver?.disconnect();
      clearTimeout(hideTimer);
      clearTimeout(noteTimer);
      document.documentElement.classList.remove('kv-cart-open');
      document.body?.classList.remove('kv-cart-open');
    },
  };
})();
